/**
 * Effects Store
 * Manages audio effects state and provides actions for effect manipulation
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { EffectsManager } from '~/lib/audio/EffectsManager';
import type {
  Effect,
  EffectType,
  EffectsChain,
  EffectParametersMap,
} from '~/types/effects';

interface EffectsState {
  // State
  effectsManager: EffectsManager | null;
  effectsChains: Map<number, EffectsChain>;
  selectedDeviceIndex: number | null;

  // Actions
  initializeEffects: () => void;
  addEffect: (deviceIndex: number, type: EffectType) => Effect | null;
  removeEffect: (deviceIndex: number, effectId: string) => void;
  updateEffectParameters: <T extends EffectType>(
    deviceIndex: number,
    effectId: string,
    parameters: Partial<EffectParametersMap[T]>
  ) => void;
  toggleEffectBypass: (deviceIndex: number, effectId: string) => void;
  toggleChainBypass: (deviceIndex: number) => void;
  reorderEffects: (deviceIndex: number, fromIndex: number, toIndex: number) => void;
  selectDevice: (deviceIndex: number) => void;
  getEffectsForDevice: (deviceIndex: number) => Effect[];
  getEffectsChain: (deviceIndex: number) => EffectsChain | undefined;
  cleanup: () => void;
}

export const useEffectsStore = create<EffectsState>()(
  immer((set, get) => ({
    // Initial state
    effectsManager: null,
    effectsChains: new Map(),
    selectedDeviceIndex: 0,

    // Initialize effects system
    initializeEffects: () => {
      let { effectsManager } = get();

      // Create manager if it doesn't exist
      if (!effectsManager) {
        effectsManager = new EffectsManager();
        set((state) => {
          state.effectsManager = effectsManager;
        });
      }

      // Create default chains for first 2 devices (virtual synths)
      for (let i = 0; i < 2; i++) {
        const chain = effectsManager.createEffectsChain(i);
        set((state) => {
          state.effectsChains.set(i, chain);
        });
      }

      console.log('🎛️ [EFFECTS] Effects system initialized');
    },

    // Add an effect to a device's chain
    addEffect: (deviceIndex: number, type: EffectType) => {
      const { effectsManager } = get();

      if (!effectsManager) {
        console.warn('[EFFECTS] Effects manager not initialized');
        return null;
      }

      try {
        const effect = effectsManager.addEffect(deviceIndex, type);

        // Update local state
        set((state) => {
          const chain = effectsManager.getEffectsChain(deviceIndex);
          if (chain) {
            state.effectsChains.set(deviceIndex, chain);
          }
        });

        console.log(`🎛️ [EFFECTS] Added ${type} effect to device ${deviceIndex}`);
        return effect;
      } catch (error) {
        console.error(`Failed to add effect: ${error}`);
        return null;
      }
    },

    // Remove an effect
    removeEffect: (deviceIndex: number, effectId: string) => {
      const { effectsManager } = get();

      if (!effectsManager) return;

      effectsManager.removeEffect(deviceIndex, effectId);

      // Update local state
      set((state) => {
        const chain = effectsManager.getEffectsChain(deviceIndex);
        if (chain) {
          state.effectsChains.set(deviceIndex, chain);
        }
      });

      console.log(`🎛️ [EFFECTS] Removed effect ${effectId} from device ${deviceIndex}`);
    },

    // Update effect parameters
    updateEffectParameters: (deviceIndex, effectId, parameters) => {
      const { effectsManager } = get();

      if (!effectsManager) return;

      effectsManager.updateEffectParameters(deviceIndex, effectId, parameters);

      // Update local state
      set((state) => {
        const chain = effectsManager.getEffectsChain(deviceIndex);
        if (chain) {
          state.effectsChains.set(deviceIndex, chain);
        }
      });
    },

    // Toggle effect bypass
    toggleEffectBypass: (deviceIndex: number, effectId: string) => {
      const { effectsManager } = get();

      if (!effectsManager) return;

      effectsManager.toggleEffectBypass(deviceIndex, effectId);

      // Update local state
      set((state) => {
        const chain = effectsManager.getEffectsChain(deviceIndex);
        if (chain) {
          state.effectsChains.set(deviceIndex, chain);
        }
      });
    },

    // Toggle entire chain bypass
    toggleChainBypass: (deviceIndex: number) => {
      const { effectsManager } = get();

      if (!effectsManager) return;

      effectsManager.toggleChainBypass(deviceIndex);

      // Update local state
      set((state) => {
        const chain = effectsManager.getEffectsChain(deviceIndex);
        if (chain) {
          state.effectsChains.set(deviceIndex, chain);
        }
      });
    },

    // Reorder effects in chain
    reorderEffects: (deviceIndex: number, fromIndex: number, toIndex: number) => {
      set((state) => {
        const chain = state.effectsChains.get(deviceIndex);
        if (!chain) return;

        const effects = [...chain.effects];
        const [removed] = effects.splice(fromIndex, 1);
        effects.splice(toIndex, 0, removed);

        chain.effects = effects;
        state.effectsChains.set(deviceIndex, chain);

        // Rebuild routing in effects manager
        // Note: This requires adding a method to EffectsManager or rebuilding manually
        console.log(`🎛️ [EFFECTS] Reordered effects for device ${deviceIndex}`);
      });
    },

    // Select a device for editing
    selectDevice: (deviceIndex: number) => {
      set((state) => {
        state.selectedDeviceIndex = deviceIndex;
      });
    },

    // Get effects for a specific device
    getEffectsForDevice: (deviceIndex: number) => {
      const chain = get().effectsChains.get(deviceIndex);
      return chain ? chain.effects : [];
    },

    // Get entire effects chain for a device
    getEffectsChain: (deviceIndex: number) => {
      return get().effectsChains.get(deviceIndex);
    },

    // Cleanup
    cleanup: () => {
      const { effectsManager } = get();

      if (effectsManager) {
        effectsManager.dispose();
      }

      set((state) => {
        state.effectsChains.clear();
        state.selectedDeviceIndex = null;
        state.effectsManager = null;
      });

      console.log('🎛️ [EFFECTS] Effects system cleaned up');
    },
  }))
);

// Auto-initialize on store creation (skip in test environment)
if (typeof window !== 'undefined' && !import.meta.env.TEST) {
  // Only initialize if we're in a browser and not in test mode
  // Use requestAnimationFrame to ensure DOM is ready
  requestAnimationFrame(() => {
    try {
      useEffectsStore.getState().initializeEffects();
    } catch (error) {
      console.warn('[EFFECTS] Failed to auto-initialize:', error);
    }
  });
}